import { Form, Formik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { InfoWithLabel } from "@/core/components/info-with-label";
import { AutoComplete } from "@/core/components/ui/autocomplete";
import { Button } from "@/core/components/ui/button";
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/core/components/ui/dialog";
import { Dialog } from "@/core/components/ui/dialog";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { IAchAccount } from "@/internal-transactions/InternalTransactions";
import {
  useGetAchAccount,
  useSetAchAccount,
} from "@/internal-transactions/query";

interface NewBankDetailsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  businessId?: string | null;
}

const schema = yup.object().shape({
  account_type: yup.string().required(),
  account_number: yup
    .string()
    .required()
    .matches(/^\d+$/, "Account number must contain only digits"),
  confirm_account_number: yup
    .string()
    .required()
    .matches(/^\d+$/, "Account number must contain only digits")
    .oneOf([yup.ref("account_number")], "Account numbers must match"),
  routing_number: yup
    .string()
    .required()
    .matches(/^\d+$/, "Routing number must contain only digits")
    .length(9, "Routing number must be exactly 9 digits"),
});

const ACCOUNT_TYPE_OPTIONS = [
  { label: "Checking", value: "checking" },
  { label: "Savings", value: "savings" },
];

export const NewBankDetailsModal = ({
  isOpen,
  onOpenChange,
  businessId,
}: NewBankDetailsModalProps) => {
  const [accountTypeSearchValue, setAccountTypeSearchValue] = useState("");

  const { data: achAccount } = useGetAchAccount(businessId);
  const { mutate: setAchAccount } = useSetAchAccount(businessId);

  const overwritingBankDetails = !!achAccount; // if the user already has ach acct info they are overwriting their current info with this form

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        description={
          overwritingBankDetails ? "New bank details" : "Add bank details"
        }
      >
        <Formik
          initialValues={{
            account_type: "checking",
            account_number: "",
            confirm_account_number: "",
            routing_number: "",
          }}
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting }) => {
            setAchAccount(values, {
              onSuccess: () => {
                toast.success("Bank details saved");
                onOpenChange(false);
              },
              onError: () => {
                toast.error("Error saving bank details");
              },
            });
            setSubmitting(false);
          }}
        >
          {({
            values,
            errors,
            isSubmitting,
            isValid,
            handleChange,
            getFieldProps,
            touched,
          }) => (
            <Form>
              <DialogHeader className="border border-stroke-tertiary text-left">
                <Typography variant="subtitleW1">
                  {overwritingBankDetails
                    ? "Edit banking details"
                    : "Add banking details"}
                </Typography>
              </DialogHeader>
              <Stack className="p-6" gap="lg" fullWidth>
                {overwritingBankDetails && (
                  <>
                    <CurrentBankDetails achAccount={achAccount} />
                    <Separator />
                    <Stack gap="sm" fullWidth>
                      <Typography variant="subtitleW2">
                        New account details
                      </Typography>
                      <Typography variant="bodyW2" textColor="negative">
                        Note: This update will replace your current linked bank
                        account in Volca.
                      </Typography>
                    </Stack>
                  </>
                )}
                <AutoComplete
                  label="Account Type"
                  placeholder="Account Type"
                  items={ACCOUNT_TYPE_OPTIONS}
                  selectedValue={values.account_type}
                  onSelectedValueChange={handleChange("account_type")}
                  searchValue={accountTypeSearchValue}
                  onSearchValueChange={setAccountTypeSearchValue}
                  required
                  fullWidth
                />
                <FormInput
                  label="Account number"
                  {...getFieldProps("account_number")}
                  required
                  fullWidth
                />
                <FormInput
                  label="Confirm account number"
                  {...getFieldProps("confirm_account_number")}
                  required
                  fullWidth
                  className={
                    values.confirm_account_number &&
                    values.account_number &&
                    touched.confirm_account_number &&
                    errors.confirm_account_number
                      ? "mb-4"
                      : ""
                  }
                  error={
                    values.confirm_account_number &&
                    values.account_number &&
                    touched.confirm_account_number &&
                    errors.confirm_account_number
                      ? errors.confirm_account_number
                      : undefined
                  }
                />
                <FormInput
                  label="Routing number"
                  {...getFieldProps("routing_number")}
                  required
                  fullWidth
                  maxLength={9}
                />
              </Stack>
              <DialogFooter>
                <Button
                  variant="tertiary"
                  type="button"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid}
                  loading={isSubmitting}
                >
                  Save
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

interface CurrentBankDetailsProps {
  achAccount: IAchAccount;
}

const CurrentBankDetails = ({ achAccount }: CurrentBankDetailsProps) => (
  <Stack fullWidth>
    <Typography variant="subtitleW2">Current bank details</Typography>
    <Stack direction="row" gap="lg" fullWidth>
      <InfoWithLabel label="Account name">{achAccount.name}</InfoWithLabel>
      <InfoWithLabel label="Account number">
        {`*******${achAccount.mask}`}
      </InfoWithLabel>
    </Stack>
  </Stack>
);
